import { onIdTokenChanged, type User } from "firebase/auth";

import { auth } from "@/components/context/firebase";
import { API_URL } from "@/components/voice-room/lib/config";
import publicBlogPages from "@/lib/analytics/analytics-blog-pages.json";
import {
  getAnalyticsConsent,
  hasAnalyticsConsent,
  subscribeToAnalyticsConsent,
} from "@/lib/analytics/consent";
import { CANDIDATE_LANG_CODES, CANDIDATE_SLUGS, nativeCandidatePath } from "@/lib/native/candidatePages.js";

export type WebsiteDevice = "mobile" | "tablet" | "desktop";
export type WebsiteAction =
  | "click"
  | "outbound_click"
  | "download_click"
  | "rage_click"
  | "form_start"
  | "form_submit"
  | "form_invalid"
  | "scroll"
  | "engagement"
  | "error"
  | "performance"
  | "audience_selected"
  | "signup_started"
  | "signup_completed"
  | "login_completed"
  | "interview_started"
  | "interview_completed"
  | "interview_error"
  | "permission_denied"
  | "product_event"
  | "demo_booked";
export type WebsiteActionFields = {
  target?: string;
  metric?: string;
  audience?: "hr" | "candidate";
  value?: number;
  target_path?: string;
  target_host?: string;
  target_content?: string;
  error_message?: string;
  error_source?: string;
};
export type WebsiteTrackingContext = {
  visitorId: string;
  sessionId: string;
  pageViewId: string;
  path: string;
};
export type WebsiteEventName =
  | "website_page_view"
  | "website_page_exit"
  | "website_identity"
  | "website_action";

type BrowserName = "chrome" | "edge" | "firefox" | "safari" | "other";
type OperatingSystem = "windows" | "macos" | "android" | "ios" | "linux" | "other";

type Acquisition = {
  source: string;
  medium: string;
  campaign: string;
  term: string;
  content: string;
  landing_path: string;
  referrer_host: string;
  google_click_id_present: boolean;
};

type StoredSession = Acquisition & {
  session_id: string;
  last_activity: number;
};

type WebsiteEvent = Acquisition & {
  event_id: string;
  name: WebsiteEventName;
  t: number;
  page_view_id: string;
  path: string;
  device: WebsiteDevice;
  browser: BrowserName;
  os: OperatingSystem;
  language?: string;
  viewport_width?: number;
  viewport_height?: number;
  exit_reason?: "navigation" | "pagehide" | "hidden";
  duration_ms?: number;
  action?: WebsiteAction;
  target?: string;
  metric?: string;
  audience?: "hr" | "candidate";
  value?: number;
  target_path?: string;
  target_host?: string;
  target_content?: string;
  error_message?: string;
  error_source?: string;
  company_slug?: string;
  role_slug?: string;
};

type PageContext = {
  visitorId: string;
  session: StoredSession;
};

type Delivery = {
  body: string;
  eventIds: string[];
  authToken: string | null;
  authUid: string | null;
  accountGeneration: number;
};

type PendingAuthAction = {
  delivery: Delivery;
  uid: string;
  consentGeneration: number;
  accountGeneration: number;
};

const VISITOR_COOKIE = "aiia_visitor_id";
const SESSION_KEY = "aiia_website_session";
const VISITOR_MAX_AGE = 180 * 24 * 60 * 60;
const SESSION_TIMEOUT = 30 * 60 * 1000;
const MAX_DURATION = 24 * 60 * 60 * 1000;
const MAX_RETRIES = 2;
const MAX_ACTIONS_PER_MINUTE = 60;
const MAX_TOTAL_ACTIONS_PER_MINUTE = 100;
const MAX_PENDING_AUTH_ACTIONS = 10;
const MAX_ERROR_ACTIONS_PER_PAGE = 5;
const MAX_PERFORMANCE_ACTIONS_PER_PAGE = 8;
const SAFE_IDENTIFIER = /^[a-z][a-z0-9_]{0,79}$/;
const ERROR_URL = /[A-Za-z][A-Za-z0-9+.-]*:\/\/\S+/g;
const ERROR_EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const ERROR_NUMBER = /\d{6,}/g;
const ERROR_SOURCE = /^[A-Za-z0-9._-]{1,120}(?::\d{1,7}){0,2}$/;
const MAX_ERROR_MESSAGE = 200;
const ACTIONS = new Set<WebsiteAction>([
  "click", "outbound_click", "download_click", "rage_click", "form_start",
  "form_submit", "form_invalid", "scroll", "engagement", "error",
  "performance", "audience_selected", "signup_started", "signup_completed",
  "login_completed", "interview_started", "interview_completed", "interview_error",
  "permission_denied", "product_event", "demo_booked",
]);
const CRITICAL_ACTIONS = new Set<WebsiteAction>([
  "audience_selected", "signup_started", "signup_completed", "login_completed",
  "interview_started", "interview_completed", "interview_error", "permission_denied",
  "demo_booked",
]);
const AUTHENTICATED_ACTIONS = new Set<WebsiteAction>([
  "signup_completed", "login_completed",
]);

const ROUTES = [
  "/", "/about", "/candidates", "/contact", "/newsletter", "/book-a-demo", "/pricing", "/privacy-policy", "/terms-of-use", "/refund-policy",
  "/report-preview", "/shared-video", "/shared-clip", "/connect",
  "/de", "/fr", "/es", "/sv", "/it",
  ...CANDIDATE_LANG_CODES.flatMap((code) => CANDIDATE_SLUGS.map((slug) => nativeCandidatePath(code, slug))),
  "/demo/post-interview-feedback", "/blog", "/blog/:slug",
  "/interview-questions", "/interview-questions/:slug", "/kits/:slug",
  "/compare", "/compare/:slug", "/industries", "/industries/:slug",
  "/integrations", "/integrations/:slug", "/security", "/languages",
  "/tools", "/tools/resume-interview-questions", "/tools/interview-kit-generator",
  "/tools/boolean-search-generator", "/tools/job-description-generator", "/tools/reference-check-questions", "/tools/ai-resume-checker", "/tools/cost-per-hire-calculator", "/auth", "/auth/login",
  "/auth/register", "/certificate/:id", "/interview/:meetingId", "/i/:meetingId",
  "/careers/:slug", "/careers/:slug/:roleSlug", "/my-interviews", "/dashboard", "/recruiters",
  "/recruiters/ai-interview-bot", "/recruiters/ai-interview-software",
  "/recruiters/ai-interviewer", "/recruiters/ai-phone-screening",
  "/recruiters/ai-video-interview-software", "/recruiters/interview-highlight-clips",
  "/recruiters/candidate-location-check", "/recruiters/joining-readiness",
  "/recruiters/call-to-submittal",
  "/recruiters/ai-mock-interviewer", "/recruiters/ai-resume-screening",
  "/recruiters/agentic-resume-search", "/recruiters/career-page-builder",
  "/features/ai-interviewer", "/features/ai-mock-interview", "/features/ai-video-interview",
  "/features/interview-warmup", "/dashboard/agency", "/dashboard/agency/agents",
  "/dashboard/agency/assets", "/dashboard/agency/assets/:roleId",
  "/dashboard/agency/candidates", "/dashboard/agency/create-interview",
  "/dashboard/agency/data", "/dashboard/agency/data/:folderId",
  "/dashboard/agency/feedback/:id", "/dashboard/agency/interview-room",
  "/dashboard/agency/interviews", "/dashboard/agency/interviews/:roleId",
  "/dashboard/agency/interviews/pipeline/:roleId", "/dashboard/agency/meeting-login",
  "/dashboard/agency/modify-interview", "/dashboard/agency/profile",
  "/dashboard/agency/report/:id", "/dashboard/agency/reports",
  "/dashboard/agency/reports/:roleId", "/dashboard/agency/reviews",
  "/dashboard/agency/reviews/:roleId", "/dashboard/agency/screening",
  "/dashboard/agency/screening/new", "/dashboard/agency/screening/:roleId",
  "/dashboard/agency/roles", "/dashboard/agency/roles/new",
  "/dashboard/agency/roles/:roleId/edit", "/dashboard/agency/roles/:roleId/interview-setup",
  "/dashboard/agency/roles/:roleId", "/dashboard/agency/company",
  "/dashboard/agency/company/career-page", "/dashboard/agency/company/emails",
  "/dashboard/agency/company/integrations", "/dashboard/agency/branding",
  "/dashboard/agency/branding/career-page", "/dashboard/agency/branding/emails",
  "/dashboard/agency/branding/integrations", "/dashboard/agency/careers-settings",
  "/dashboard/agency/talent-pool", "/dashboard/agency/integrations",
  "/dashboard/admin", "/dashboard/admin/activity-tracking", "/dashboard/admin/analytics",
  "/dashboard/admin/hr-management", "/dashboard/admin/interview-results",
  "/dashboard/candidate", "/dashboard/candidate/applications",
  "/dashboard/candidate/agent-confirmation", "/dashboard/candidate/agents",
  "/dashboard/candidate/create-agent", "/dashboard/candidate/interview-prep",
  "/dashboard/candidate/jobs", "/dashboard/candidate/jobs/:id",
  "/dashboard/candidate/leaderboard", "/dashboard/candidate/profile",
  "/dashboard/candidate/report/:id", "/dashboard/candidate/reports",
  "/dashboard/candidate/resume-builder-1786877876754", "/dashboard/candidate/role-skills",
  "/dashboard/candidate/roles", "/dashboard/candidate/templates",
] as const;

const routeMatchers = ROUTES.map((template) => ({
  template,
  segments: template.split("/").filter(Boolean),
})).sort((a, b) => {
  const aDynamic = a.segments.filter((segment) => segment.startsWith(":")).length;
  const bDynamic = b.segments.filter((segment) => segment.startsWith(":")).length;
  return aDynamic - bDynamic || b.segments.length - a.segments.length;
});

export const routeTemplateForPath = (pathname: string): string => {
  const canonical = pathname.replace(/\/+$/, "") || "/";
  if (Object.prototype.hasOwnProperty.call(publicBlogPages, canonical)) return canonical;
  const segments = canonical.split("/").filter(Boolean);
  const match = routeMatchers.find(({ segments: pattern }) =>
    pattern.length === segments.length && pattern.every((part, index) =>
      part.startsWith(":") || part === segments[index]
    )
  );
  return match?.template ?? "/:unknown";
};

export const sanitizeAttributionLabel = (value: string | null, max: number): string => {
  if (!value) return "none";
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > max || !/^[A-Za-z0-9 _.-]+$/.test(trimmed)) return "none";
  return trimmed;
};

export const deviceCategory = (userAgent: string): WebsiteDevice => {
  if (/iPad|Tablet|PlayBook|Silk|Android(?!.*Mobile)/i.test(userAgent)) return "tablet";
  if (/Mobi|iPhone|iPod|Android/i.test(userAgent)) return "mobile";
  return "desktop";
};

const browserName = (userAgent: string): BrowserName => {
  if (/Edg(?:e|A|iOS)?\//i.test(userAgent)) return "edge";
  if (/Firefox\/|FxiOS\//i.test(userAgent)) return "firefox";
  if (/Chrome\/|CriOS\//i.test(userAgent)) return "chrome";
  if (/Safari\//i.test(userAgent) && /Version\//i.test(userAgent)) return "safari";
  return "other";
};

const operatingSystem = (userAgent: string): OperatingSystem => {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  if (/Windows/i.test(userAgent)) return "windows";
  if (/Mac OS X|Macintosh/i.test(userAgent)) return "macos";
  if (/Linux/i.test(userAgent)) return "linux";
  return "other";
};

const safeLanguage = (value: string | undefined): string | undefined => {
  if (!value || value.length > 30 || !/^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8}){0,2}$/.test(value)) {
    return undefined;
  }
  try {
    return Intl.getCanonicalLocales(value)[0];
  } catch {
    return undefined;
  }
};

const viewportBucket = (value: number): number | undefined => {
  if (!Number.isFinite(value) || value <= 0) return undefined;
  return Math.min(10_000, Math.ceil(value / 100) * 100);
};

const browserContext = (): Pick<WebsiteEvent, "browser" | "os" | "language" | "viewport_width" | "viewport_height"> => {
  const language = safeLanguage(navigator.language);
  const viewportWidth = viewportBucket(window.innerWidth);
  const viewportHeight = viewportBucket(window.innerHeight);
  return {
    browser: browserName(navigator.userAgent),
    os: operatingSystem(navigator.userAgent),
    ...(language ? { language } : {}),
    ...(viewportWidth ? { viewport_width: viewportWidth } : {}),
    ...(viewportHeight ? { viewport_height: viewportHeight } : {}),
  };
};

const ownHost = (host: string): boolean =>
  host === window.location.hostname || host === "aiinterviewagents.com" || host.endsWith(".aiinterviewagents.com");

const captureAcquisition = (): Acquisition => {
  const params = new URLSearchParams(window.location.search);
  const googleClick = ["gclid", "gbraid", "wbraid"].some((key) => params.has(key));
  let referrerHost = "none";
  try {
    const host = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : "";
    if (host && !ownHost(host)) referrerHost = host.slice(0, 253);
  } catch { /* An invalid referrer is discarded. */ }
  const explicitSource = sanitizeAttributionLabel(params.get("utm_source"), 80);
  const googleReferrer = /(^|\.)google\.(?:com|[a-z]{2,3}|co\.[a-z]{2}|com\.[a-z]{2})$/.test(referrerHost);
  const externalSource = sanitizeAttributionLabel(referrerHost, 80);
  return {
    source: explicitSource !== "none"
      ? explicitSource
      : googleClick || googleReferrer
        ? "google"
        : externalSource !== "none"
          ? externalSource
          : "direct",
    medium: sanitizeAttributionLabel(params.get("utm_medium"), 80),
    campaign: sanitizeAttributionLabel(params.get("utm_campaign"), 120),
    term: sanitizeAttributionLabel(params.get("utm_term"), 80),
    content: sanitizeAttributionLabel(params.get("utm_content"), 120),
    landing_path: routeTemplateForPath(window.location.pathname),
    referrer_host: referrerHost,
    google_click_id_present: googleClick,
  };
};

let initialAcquisition: Acquisition | null = typeof window === "undefined" ? null : captureAcquisition();
let generation = 0;
let started = false;
let subscribers = 0;
let visitorId: string | null = null;
let session: StoredSession | null = null;
let cachedToken: string | null = null;
let activeUserUid: string | null = null;
let identityUser: string | null = null;
let accountGeneration = 0;
let current: { pathname: string; template: string; id: string; startedAt: number; exited: boolean; context: PageContext } | null = null;
let pendingPath: { pathname: string; template: string } | null = null;
let retryTimers = new Map<ReturnType<typeof setTimeout>, string | null>();
let retryEventIds = new Set<string>();
let controllers = new Map<AbortController, string | null>();
let unsubscribeConsent: (() => void) | null = null;
let unsubscribeAuth: (() => void) | null = null;
let pendingAuthActions: PendingAuthAction[] = [];
let ordinaryActionTimes: number[] = [];
let totalActionTimes: number[] = [];
let limitedPageViewId = "";
let errorActionCount = 0;
let performanceActionCount = 0;

const accepted = (): boolean => hasAnalyticsConsent();
const uuid = (): string => crypto.randomUUID();
const trackerSupported = (): boolean =>
  typeof window !== "undefined" &&
  typeof document !== "undefined" &&
  typeof globalThis.crypto?.randomUUID === "function" &&
  typeof globalThis.fetch === "function" &&
  typeof globalThis.AbortController === "function";
const isUuid = (value: unknown): value is string =>
  typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const readCookie = (): string | null => {
  try {
    const prefix = `${VISITOR_COOKIE}=`;
    const value = document.cookie.split(";").map((part) => part.trim())
      .find((part) => part.startsWith(prefix))?.slice(prefix.length);
    return isUuid(value) ? value : null;
  } catch {
    return null;
  }
};

const ensureVisitor = (): string => {
  if (visitorId) return visitorId;
  visitorId = readCookie() || uuid();
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${VISITOR_COOKIE}=${visitorId}; Max-Age=${VISITOR_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  } catch { /* The consented in-memory visitor remains usable. */ }
  return visitorId;
};

// A session stored before term, content and landing_path existed has none of them.
const validAcquisition = (value: Partial<Acquisition>): Acquisition | null => {
  const term = value.term ?? "none";
  const content = value.content ?? "none";
  const landingPath = value.landing_path ?? pendingPath?.template ?? routeTemplateForPath(window.location.pathname);
  if (sanitizeAttributionLabel(term, 80) !== term ||
      sanitizeAttributionLabel(content, 120) !== content ||
      routeTemplateForPath(landingPath) !== landingPath) return null;
  if (typeof value.source !== "string" || typeof value.medium !== "string" ||
      typeof value.campaign !== "string" || typeof value.referrer_host !== "string" ||
      typeof value.google_click_id_present !== "boolean" ||
      sanitizeAttributionLabel(value.source, 80) !== value.source ||
      sanitizeAttributionLabel(value.medium, 80) !== value.medium ||
      sanitizeAttributionLabel(value.campaign, 120) !== value.campaign ||
      (value.referrer_host !== "none" && !/^[a-z0-9.-]{1,253}$/i.test(value.referrer_host))) return null;
  return { ...value, term, content, landing_path: landingPath } as Acquisition;
};

const sessionForNewPage = (now: number): StoredSession => {
  if (!session) {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null") as Partial<StoredSession> | null;
      const acquisition = parsed && validAcquisition(parsed);
      if (parsed && acquisition && isUuid(parsed.session_id) && typeof parsed.last_activity === "number") {
        session = { ...acquisition, session_id: parsed.session_id, last_activity: parsed.last_activity };
      }
    } catch { /* Corrupt optional metadata starts a fresh visit. */ }
  }
  if (!session || now - session.last_activity > SESSION_TIMEOUT) {
    // The session lands on the first page it records, not on the page that loaded the tracker.
    const acquisition = initialAcquisition ?? captureAcquisition();
    session = {
      ...acquisition,
      landing_path: pendingPath?.template ?? acquisition.landing_path,
      session_id: uuid(),
      last_activity: now,
    };
  }
  session.last_activity = now;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch { /* The consented in-memory session remains usable. */ }
  return session;
};

const touchCurrentSession = (now: number): void => {
  if (!session || session.session_id !== current?.context.session.session_id ||
      now < session.last_activity || now - session.last_activity > SESSION_TIMEOUT) return;
  session.last_activity = now;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch { /* The consented in-memory session remains usable. */ }
};

const pageContextForNewPage = (now: number): PageContext => ({
  visitorId: ensureVisitor(),
  session: { ...sessionForNewPage(now) },
});

const clearOptionalState = (): void => {
  generation += 1;
  visitorId = null;
  session = null;
  cachedToken = null;
  activeUserUid = null;
  identityUser = null;
  current = null;
  initialAcquisition = null;
  retryTimers.forEach((_uid, timer) => clearTimeout(timer));
  retryTimers.clear();
  retryEventIds.clear();
  controllers.forEach((_uid, controller) => controller.abort());
  controllers.clear();
  pendingAuthActions = [];
  ordinaryActionTimes = [];
  totalActionTimes = [];
  limitedPageViewId = "";
  errorActionCount = 0;
  performanceActionCount = 0;
  try {
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${VISITOR_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
    sessionStorage.removeItem(SESSION_KEY);
  } catch { /* Storage may be unavailable. */ }
};

const makeDelivery = (events: WebsiteEvent[], context: PageContext, token = cachedToken): Delivery => {
  const authUid = token ? activeUserUid : null;
  return {
    body: JSON.stringify({
      consent: true,
      visitor_id: context.visitorId,
      session_id: context.session.session_id,
      events: events.slice(0, 20),
    }),
    eventIds: events.map((event) => event.event_id),
    authToken: token,
    authUid,
    accountGeneration,
  };
};

const deliveryIsCurrent = (delivery: Delivery, expectedGeneration: number): boolean =>
  accepted() &&
  expectedGeneration === generation &&
  (!delivery.authUid ||
    (delivery.authUid === activeUserUid && delivery.accountGeneration === accountGeneration));

const send = (delivery: Delivery, attempt = 0, expectedGeneration = generation): void => {
  if (!deliveryIsCurrent(delivery, expectedGeneration) || !delivery.eventIds.length) return;
  let controller: AbortController;
  try {
    controller = new AbortController();
  } catch {
    return;
  }
  controllers.set(controller, delivery.authUid);
  void Promise.resolve().then(() => fetch(`${API_URL}/website/events/`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(delivery.authToken ? { Authorization: `Bearer ${delivery.authToken}` } : {}) },
      body: delivery.body,
      signal: controller.signal,
    })).then((response) => {
    if (!response.ok) throw new Error(`website analytics ${response.status}`);
    delivery.eventIds.forEach((eventId) => retryEventIds.delete(eventId));
  }).catch(() => {
    if (attempt >= MAX_RETRIES || !deliveryIsCurrent(delivery, expectedGeneration)) {
      delivery.eventIds.forEach((eventId) => retryEventIds.delete(eventId));
      return;
    }
    const newIds = delivery.eventIds.filter((eventId) => !retryEventIds.has(eventId));
    if (retryEventIds.size + newIds.length > 20) return;
    newIds.forEach((eventId) => retryEventIds.add(eventId));
    const timer = setTimeout(() => {
      retryTimers.delete(timer);
      send(delivery, attempt + 1, expectedGeneration);
    }, 500 * (attempt + 1));
    retryTimers.set(timer, delivery.authUid);
  }).finally(() => controllers.delete(controller));
};

const CAREER_SLUG = /^[a-z0-9][a-z0-9-]{0,119}$/;

// Career pages are public, so their company and role slugs travel with the
// event; every other page keeps only its route template.
const careerSlugs = (template: string): Pick<WebsiteEvent, "company_slug" | "role_slug"> => {
  if (!current || (template !== "/careers/:slug" && template !== "/careers/:slug/:roleSlug")) return {};
  const [, companySlug, roleSlug] = current.pathname.split("/").filter(Boolean);
  if (!companySlug || !CAREER_SLUG.test(companySlug)) return {};
  return roleSlug && CAREER_SLUG.test(roleSlug)
    ? { company_slug: companySlug, role_slug: roleSlug }
    : { company_slug: companySlug };
};

const makeEvent = (name: WebsiteEventName, pageViewId: string, path: string, now: number, context: PageContext): WebsiteEvent => {
  const activeSession = context.session;
  return {
    ...careerSlugs(path),
    event_id: uuid(), name, t: Math.trunc(now), page_view_id: pageViewId, path,
    device: deviceCategory(navigator.userAgent), source: activeSession.source,
    medium: activeSession.medium, campaign: activeSession.campaign,
    term: activeSession.term, content: activeSession.content,
    landing_path: activeSession.landing_path,
    referrer_host: activeSession.referrer_host,
    google_click_id_present: activeSession.google_click_id_present,
    ...browserContext(),
  };
};

const emitExit = (reason: "navigation" | "pagehide" | "hidden"): void => {
  if (!accepted() || !current || current.exited) return;
  current.exited = true;
  const now = Date.now();
  const event = { ...makeEvent("website_page_exit", current.id, current.template, now, current.context),
    exit_reason: reason, duration_ms: Math.min(MAX_DURATION, Math.max(0, now - current.startedAt)) };
  const delivery = makeDelivery([event], current.context);
  if (reason !== "navigation") {
    try {
      if (delivery.authToken) {
        void fetch(`${API_URL}/website/events/`, { method: "POST", keepalive: true,
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${delivery.authToken}` }, body: delivery.body });
      } else if (navigator.sendBeacon && typeof Blob === "function") {
        // text/plain keeps the beacon CORS-simple: a JSON beacon needs a
        // credentialed preflight the API does not allow, so the browser drops it.
        navigator.sendBeacon(`${API_URL}/website/events/`, new Blob([delivery.body], { type: "text/plain;charset=UTF-8" }));
      } else send(delivery);
    } catch { /* Optional unload telemetry must never break page lifecycle. */ }
  } else send(delivery);
};

const recordPendingPage = (): void => {
  if (!accepted() || !pendingPath) return;
  const { pathname, template } = pendingPath;
  if (current?.pathname === pathname && !current.exited) return;
  if (current && current.pathname !== pathname) emitExit("navigation");
  const now = Date.now();
  const context = pageContextForNewPage(now);
  current = { pathname, template, id: uuid(), startedAt: now, exited: false, context };
  send(makeDelivery([makeEvent("website_page_view", current.id, template, now, context)], context));
};

const invalidatePriorAccount = (nextUid: string | null): void => {
  accountGeneration += 1;
  cachedToken = null;
  identityUser = null;
  retryTimers.forEach((_authUid, timer) => {
    clearTimeout(timer);
  });
  retryTimers.clear();
  retryEventIds.clear();
  pendingAuthActions = [];
  controllers.forEach((authUid, controller) => {
    if (authUid && authUid !== nextUid) controller.abort();
  });
};

const drainPendingAuthActions = (uid: string, token: string): void => {
  const ready = pendingAuthActions;
  pendingAuthActions = [];
  for (const pending of ready) {
    if (pending.uid !== uid || pending.consentGeneration !== generation ||
        pending.accountGeneration !== accountGeneration) continue;
    send({
      ...pending.delivery,
      authToken: token,
      authUid: uid,
      accountGeneration: pending.accountGeneration,
    }, 0, pending.consentGeneration);
  }
};

const handleUser = (user: User | null): void => {
  const nextUid = user?.uid ?? null;
  if (nextUid !== activeUserUid) {
    invalidatePriorAccount(nextUid);
    activeUserUid = nextUid;
  }
  if (!user || !accepted()) return;

  const expectedGeneration = generation;
  const expectedAccountGeneration = accountGeneration;
  const uid = user.uid;
  let tokenPromise: Promise<string>;
  try {
    tokenPromise = user.getIdToken();
  } catch {
    return;
  }
  void tokenPromise.then((token) => {
    if (!accepted() || expectedGeneration !== generation ||
        expectedAccountGeneration !== accountGeneration ||
        activeUserUid !== uid || auth.currentUser?.uid !== uid) return;
    cachedToken = token;
    drainPendingAuthActions(uid, token);
    if (identityUser === uid || !current) return;
    identityUser = uid;
    const event = makeEvent("website_identity", current.id, current.template, Date.now(), current.context);
    send(makeDelivery([event], current.context, token), 0, expectedGeneration);
  }).catch(() => undefined);
};

export const getWebsiteTrackingContext = (): WebsiteTrackingContext | null => {
  if (!accepted() || !current) return null;
  return {
    visitorId: current.context.visitorId,
    sessionId: current.context.session.session_id,
    pageViewId: current.id,
    path: current.template,
  };
};

const withinActionBudget = (action: WebsiteAction, now: number, pageViewId: string): boolean => {
  const cutoff = now - 60_000;
  ordinaryActionTimes = ordinaryActionTimes.filter((timestamp) => timestamp > cutoff);
  totalActionTimes = totalActionTimes.filter((timestamp) => timestamp > cutoff);
  if (totalActionTimes.length >= MAX_TOTAL_ACTIONS_PER_MINUTE) return false;
  if (!CRITICAL_ACTIONS.has(action) && ordinaryActionTimes.length >= MAX_ACTIONS_PER_MINUTE) return false;

  if (limitedPageViewId !== pageViewId) {
    limitedPageViewId = pageViewId;
    errorActionCount = 0;
    performanceActionCount = 0;
  }
  if (action === "error") {
    if (errorActionCount >= MAX_ERROR_ACTIONS_PER_PAGE) return false;
    errorActionCount += 1;
  }
  if (action === "performance") {
    if (performanceActionCount >= MAX_PERFORMANCE_ACTIONS_PER_PAGE) return false;
    performanceActionCount += 1;
  }

  totalActionTimes.push(now);
  if (!CRITICAL_ACTIONS.has(action)) ordinaryActionTimes.push(now);
  return true;
};

const safeIdentifier = (value: unknown): string | undefined =>
  typeof value === "string" && SAFE_IDENTIFIER.test(value) ? value : undefined;

// Script error text loses addresses, links and long numbers before it leaves the browser.
const scrubErrorMessage = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const message = value.slice(0, 1_000)
    .replace(ERROR_URL, "[url]")
    .replace(ERROR_EMAIL, "[email]")
    .replace(ERROR_NUMBER, "[number]")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_ERROR_MESSAGE);
  return message || undefined;
};

const safeTargetPath = (value: unknown): string | undefined => {
  if (typeof value !== "string" || !value || value.length > 2_000) return undefined;
  try {
    const target = new URL(value, window.location.origin);
    if (!/^https?:$/.test(target.protocol) || !ownHost(target.hostname.toLowerCase())) return undefined;
    return routeTemplateForPath(target.pathname);
  } catch {
    return undefined;
  }
};

const authenticatedDelivery = (
  delivery: Delivery,
  uid: string,
  token: string,
  actionAccountGeneration: number
): Delivery => ({
  ...delivery,
  authToken: token,
  authUid: uid,
  accountGeneration: actionAccountGeneration,
});

export const trackWebsiteAction = (action: WebsiteAction, fields: WebsiteActionFields = {}): void => {
  if (!accepted() || !current || !ACTIONS.has(action)) return;
  try {
    const now = Date.now();
    if (!withinActionBudget(action, now, current.id)) return;
    touchCurrentSession(now);

    const target = safeIdentifier(fields.target);
    const metric = safeIdentifier(fields.metric);
    const audience = fields.audience === "hr" || fields.audience === "candidate"
      ? fields.audience
      : undefined;
    const value = typeof fields.value === "number" && Number.isFinite(fields.value) &&
      fields.value >= 0 && fields.value <= MAX_DURATION
      ? fields.value
      : undefined;
    const targetPath = safeTargetPath(fields.target_path);
    const targetHost = action === "outbound_click" && typeof fields.target_host === "string" &&
      /^[a-z0-9.-]{1,253}$/.test(fields.target_host)
      ? fields.target_host
      : undefined;
    const targetContent = targetHost && fields.target_content
      ? sanitizeAttributionLabel(fields.target_content, 120)
      : "none";
    const errorMessage = action === "error" ? scrubErrorMessage(fields.error_message) : undefined;
    const errorSource = action === "error" && typeof fields.error_source === "string" &&
      ERROR_SOURCE.test(fields.error_source)
      ? fields.error_source
      : undefined;
    const event = Object.freeze({
      ...makeEvent("website_action", current.id, current.template, now, current.context),
      action,
      ...(target ? { target } : {}),
      ...(metric ? { metric } : {}),
      ...(audience ? { audience } : {}),
      ...(value !== undefined ? { value } : {}),
      ...(targetPath ? { target_path: targetPath } : {}),
      ...(targetHost ? { target_host: targetHost } : {}),
      ...(targetContent !== "none" ? { target_content: targetContent } : {}),
      ...(errorMessage ? { error_message: errorMessage } : {}),
      ...(errorSource ? { error_source: errorSource } : {}),
    });
    const delivery = makeDelivery([event], current.context, null);

    if (!AUTHENTICATED_ACTIONS.has(action)) {
      send(makeDelivery([event], current.context));
      return;
    }

    const user = auth.currentUser;
    if (!user?.uid) return;
    if (user.uid !== activeUserUid) {
      invalidatePriorAccount(user.uid);
      activeUserUid = user.uid;
    }
    const actionAccountGeneration = accountGeneration;
    if (cachedToken) {
      send(authenticatedDelivery(delivery, user.uid, cachedToken, actionAccountGeneration));
      return;
    }
    pendingAuthActions.push({
      delivery,
      uid: user.uid,
      consentGeneration: generation,
      accountGeneration: actionAccountGeneration,
    });
    pendingAuthActions = pendingAuthActions.slice(-MAX_PENDING_AUTH_ACTIONS);
    handleUser(user);
  } catch {
    /* Optional telemetry must never affect the product. */
  }
};

// An "Upgrade to Pro" click. `source` says where the button sat and must be a
// lowercase snake_case word (for example "sidebar", "pricing_card", "quota_banner");
// anything else is dropped and the click is still counted.
export const trackUpgradeClick = (source: string): void =>
  trackWebsiteAction("product_event", { target: "upgrade_clicked", metric: source });

const onConsent = (): void => {
  if (getAnalyticsConsent() !== "accepted") {
    clearOptionalState();
    return;
  }
  generation += 1;
  handleUser(auth.currentUser);
  recordPendingPage();
};

const onVisibility = (): void => {
  try {
    if (document.visibilityState === "hidden") emitExit("hidden");
  } catch { /* Optional telemetry must never break page lifecycle. */ }
};
const onPageHide = (): void => {
  try {
    emitExit("pagehide");
  } catch { /* Optional telemetry must never break page lifecycle. */ }
};

export const setCurrentPage = (_template: string, pathname = window.location.pathname): void => {
  const cleanPathname = pathname.split(/[?#]/, 1)[0] || "/";
  pendingPath = { pathname: cleanPathname, template: routeTemplateForPath(cleanPathname) };
  if (started) {
    try {
      handleUser(auth.currentUser);
      recordPendingPage();
    } catch { /* Optional telemetry must never break navigation. */ }
  }
};

export const startWebsiteTracking = (): void => {
  if (!trackerSupported()) return;
  subscribers += 1;
  if (started) return;
  try {
    started = true;
    unsubscribeConsent = subscribeToAnalyticsConsent(onConsent);
    unsubscribeAuth = onIdTokenChanged(auth, handleUser);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    recordPendingPage();
  } catch {
    started = false;
    subscribers = Math.max(0, subscribers - 1);
    unsubscribeConsent?.();
    unsubscribeConsent = null;
    unsubscribeAuth?.();
    unsubscribeAuth = null;
  }
};

export const stopWebsiteTracking = (): void => {
  subscribers = Math.max(0, subscribers - 1);
  if (subscribers || !started) return;
  started = false;
  unsubscribeConsent?.();
  unsubscribeConsent = null;
  unsubscribeAuth?.();
  unsubscribeAuth = null;
  document.removeEventListener("visibilitychange", onVisibility);
  window.removeEventListener("pagehide", onPageHide);
};
